import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
  Card,
  CardContent,
  Link,
  Chip,
  Button
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import MapIcon from '@mui/icons-material/Map';
import PublicIcon from '@mui/icons-material/Public';
import config from '../../config';

const ContactCard = ({ title, children, icon }) => (
  <Card variant="outlined" sx={{ mb: 3, height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon}
        <Typography variant="h6" component="h3" sx={{ ml: 1 }}>
          {title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const ContactItem = ({ icon, primary, secondary, link, type }) => (
  <Box sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}>
    <Box sx={{ mr: 1.5, color: 'text.secondary', mt: 0.5 }}>
      {icon}
    </Box>
    <Box>
      {secondary && (
        <Typography variant="body2" color="text.secondary">
          {secondary}
        </Typography>
      )}
      <Typography variant="body1">
        {link ? (
          <Link 
            href={type === 'email' ? `mailto:${primary}` : type === 'phone' ? `tel:${primary}` : primary} 
            underline="hover"
            color="primary"
          >
            {primary}
          </Link>
        ) : (
          primary || 'Not provided'
        )}
      </Typography>
    </Box>
  </Box>
);

const ContactDetailsTab = ({ organization }) => {
  if (!organization) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No organization selected
        </Typography>
      </Box>
    );
  }
  
  // Function to check if contact section has any data
  const hasContactData = (fields) => {
    return fields.some(field => organization[field]);
  };
  
  // Function to check if values are true (handling different formats)
  const isValueTrue = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
    if (typeof value === 'number') return value !== 0;
    return false;
  };
  
  return (
    <Grid container spacing={3}>
      {/* Company/School Information */}
      <Grid item xs={12} md={6}>
        <ContactCard 
          title="Organisation Information" 
          icon={isValueTrue(organization.isSchool) ? <SchoolIcon color="primary" /> : <BusinessIcon color="secondary" />}
        >
          {/* Business Type - only for businesses */}
          {isValueTrue(organization.isBusiness) && organization.businessType && (
            <ContactItem 
              icon={<BusinessIcon fontSize="small" />}
              primary={organization.businessType}
              secondary="Business Type"
            />
          )}
          
          {/* Sales Consultant - only for businesses */}
          {isValueTrue(organization.isBusiness) && organization.salesConsultant && (
            <ContactItem 
              icon={<PersonIcon fontSize="small" />}
              primary={organization.salesConsultant.name || 'Not assigned'}
              secondary="Sales Consultant"
            />
          )}
          
          {organization.website && (
            <ContactItem 
              icon={<LinkIcon fontSize="small" />}
              primary={organization.website}
              secondary="Website"
              link={true}
            />
          )}
          
          {/* Status Chips */}
          <Box sx={{ mt: 2 }}>
            {/* For schools, show School Status instead of Active */}
            {isValueTrue(organization.isSchool) && organization.schoolStatus && (
              <Chip 
                label={organization.schoolStatus} 
                size="small" 
                color={
                  organization.schoolStatus === 'Active' ? 'success' : 
                  organization.schoolStatus === 'Inactive' ? 'default' : 
                  organization.schoolStatus === 'Reorder' ? 'warning' : 
                  'default'
                }
                sx={{ mr: 1, mb: 1 }}
              />
            )}
            {/* For businesses (but not if also a school), show Active/Inactive based on Bad Debt and Business Closed */}
            {isValueTrue(organization.isBusiness) && !isValueTrue(organization.isSchool) && (
              <Chip 
                label={
                  isValueTrue(organization.badDebt) || isValueTrue(organization.businessClosed) 
                    ? "Inactive" 
                    : "Active"
                } 
                size="small" 
                color={
                  isValueTrue(organization.badDebt) || isValueTrue(organization.businessClosed) 
                    ? "default" 
                    : "success"
                }
                sx={{ mr: 1, mb: 1 }}
              />
            )}
            {isValueTrue(organization.isSchool) && (
              <Chip 
                label="School" 
                size="small" 
                color="info" 
                sx={{ mr: 1, mb: 1 }}
              />
            )}
            {isValueTrue(organization.isBusiness) && (
              <Chip 
                label="Business" 
                size="small" 
                color="secondary" 
                sx={{ mr: 1, mb: 1 }}
              />
            )}
            {isValueTrue(organization.badDebt) && (
              <Chip 
                label="Bad Debt" 
                size="small" 
                color="error" 
                sx={{ mr: 1, mb: 1 }}
              />
            )}
            {isValueTrue(organization.businessClosed) && (
              <Chip 
                label="Business Closed" 
                size="small" 
                color="error" 
                sx={{ mr: 1, mb: 1 }}
              />
            )}
          </Box>
        </ContactCard>
      </Grid>
      
      {/* Address Information */}
      <Grid item xs={12} md={6}>
        <ContactCard 
          title="Address Information" 
          icon={<LocationOnIcon color="primary" />}
        >
          <ContactItem 
            icon={<LocationOnIcon fontSize="small" />}
            primary={organization.address}
            secondary="Street Address"
          />
          
          <ContactItem 
            icon={<LocationOnIcon fontSize="small" />}
            primary={`${organization.suburb || ''} ${organization.state || ''} ${organization.postcode || ''}`}
            secondary="Location"
          />
          
          {organization.postalAddress && !organization.sameAsStreetAddress && (
            <ContactItem 
              icon={<LocationOnIcon fontSize="small" />}
              primary={organization.postalAddress}
              secondary="Postal Address"
            />
          )}
          
          {/* Regional Information - Show for Schools */}
          {isValueTrue(organization.isSchool) && (
            <>
              {organization.state && (
                <ContactItem 
                  icon={<PublicIcon fontSize="small" />}
                  primary={organization.state}
                  secondary="State"
                />
              )}
              
              {(organization.greaterRegion || organization.region) && (
                <ContactItem 
                  icon={<MapIcon fontSize="small" />}
                  primary={organization.greaterRegion || organization.region}
                  secondary="Greater Region"
                />
              )}
              
              {(organization.subRegion || organization.region2) && (
                <ContactItem 
                  icon={<MapIcon fontSize="small" />}
                  primary={organization.subRegion || organization.region2}
                  secondary="Sub-Region (LGA)"
                />
              )}
            </>
          )}
        </ContactCard>
      </Grid>
      
      {/* Primary Contact */}
      {hasContactData(['primaryName', 'primaryEmail', 'primaryPhone', 'primaryGeneralEmail']) && (
        <Grid item xs={12} md={6}>
          <ContactCard 
            title="Primary Contact" 
            icon={<PersonIcon color="primary" />}
          >
            <ContactItem 
              icon={<PersonIcon fontSize="small" />}
              primary={organization.primaryName}
              secondary="Name"
            />
            
            {organization.primaryEmail && (
              <ContactItem 
                icon={<EmailIcon fontSize="small" />}
                primary={organization.primaryEmail}
                secondary="Email"
                link={true}
                type="email"
              />
            )}
            
            {organization.primaryPhone && (
              <ContactItem 
                icon={<PhoneIcon fontSize="small" />}
                primary={organization.primaryPhone}
                secondary="Phone"
                link={true}
                type="phone"
              />
            )}
            
            {organization.primaryGeneralEmail && (
              <ContactItem 
                icon={<EmailIcon fontSize="small" />}
                primary={organization.primaryGeneralEmail}
                secondary="General Email"
                link={true}
                type="email"
              />
            )}
          </ContactCard>
        </Grid>
      )}
      
      {/* Principal - School Specific */}
      {isValueTrue(organization.isSchool) && hasContactData(['principalName', 'principalEmail', 'principalPhone', 'principalGeneralEmail']) && (
        <Grid item xs={12} md={6}>
          <ContactCard 
            title="Principal" 
            icon={<PersonIcon color="info" />}
          >
            <ContactItem 
              icon={<PersonIcon fontSize="small" />}
              primary={organization.principalName}
              secondary="Name"
            />
            
            {organization.principalEmail && (
              <ContactItem 
                icon={<EmailIcon fontSize="small" />}
                primary={organization.principalEmail}
                secondary="Email"
                link={true}
                type="email"
              />
            )}
            
            {organization.principalPhone && (
              <ContactItem 
                icon={<PhoneIcon fontSize="small" />}
                primary={organization.principalPhone}
                secondary="Phone"
                link={true}
                type="phone"
              />
            )}
            
            {organization.principalGeneralEmail && (
              <ContactItem 
                icon={<EmailIcon fontSize="small" />}
                primary={organization.principalGeneralEmail}
                secondary="General Email"
                link={true}
                type="email"
              />
            )}
          </ContactCard>
        </Grid>
      )}
      
      {/* Business Manager - School Specific */}
      {isValueTrue(organization.isSchool) && hasContactData(['businessManagerName', 'businessManagerEmail', 'businessManagerPhone', 'businessManagerGeneralEmail']) && (
        <Grid item xs={12} md={6}>
          <ContactCard 
            title="Business Manager" 
            icon={<PersonIcon color="warning" />}
          >
            <ContactItem 
              icon={<PersonIcon fontSize="small" />}
              primary={organization.businessManagerName}
              secondary="Name"
            />
            
            {organization.businessManagerEmail && (
              <ContactItem 
                icon={<EmailIcon fontSize="small" />}
                primary={organization.businessManagerEmail}
                secondary="Email"
                link={true}
                type="email"
              />
            )}
            
            {organization.businessManagerPhone && (
              <ContactItem 
                icon={<PhoneIcon fontSize="small" />}
                primary={organization.businessManagerPhone}
                secondary="Phone"
                link={true}
                type="phone"
              />
            )}
            
            {organization.businessManagerGeneralEmail && (
              <ContactItem 
                icon={<EmailIcon fontSize="small" />}
                primary={organization.businessManagerGeneralEmail}
                secondary="General Email"
                link={true}
                type="email"
              />
            )}
          </ContactCard>
        </Grid>
      )}
      
      {/* Accounts Contact */}
      {hasContactData(['accountsName', 'accountsEmail', 'accountsPhone', 'accountsGeneralEmail']) && (
        <Grid item xs={12} md={6}>
          <ContactCard 
            title="Accounts Contact" 
            icon={<PersonIcon color="secondary" />}
          >
            <ContactItem 
              icon={<PersonIcon fontSize="small" />}
              primary={organization.accountsName}
              secondary="Name"
            />
            
            {organization.accountsEmail && (
              <ContactItem 
                icon={<EmailIcon fontSize="small" />}
                primary={organization.accountsEmail}
                secondary="Email"
                link={true}
                type="email"
              />
            )}
            
            {organization.accountsPhone && (
              <ContactItem 
                icon={<PhoneIcon fontSize="small" />}
                primary={organization.accountsPhone}
                secondary="Phone"
                link={true}
                type="phone"
              />
            )}
            
            {organization.accountsGeneralEmail && (
              <ContactItem 
                icon={<EmailIcon fontSize="small" />}
                primary={organization.accountsGeneralEmail}
                secondary="General Email"
                link={true}
                type="email"
              />
            )}
          </ContactCard>
        </Grid>
      )}
      
      {/* General Contact */}
      {hasContactData(['generalEmail', 'generalPhone']) && (
        <Grid item xs={12} md={6}>
          <ContactCard 
            title="General Contact" 
            icon={<BusinessIcon color="info" />}
          >
            {organization.generalEmail && (
              <ContactItem 
                icon={<EmailIcon fontSize="small" />}
                primary={organization.generalEmail}
                secondary="Email"
                link={true}
                type="email"
              />
            )}
            
            {organization.generalPhone && (
              <ContactItem 
                icon={<PhoneIcon fontSize="small" />}
                primary={organization.generalPhone}
                secondary="Phone"
                link={true}
                type="phone"
              />
            )}
          </ContactCard>
        </Grid>
      )}
      
      {/* Additional Contacts */}
      {organization.additionalContacts && organization.additionalContacts.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
            Additional Contacts
          </Typography>
          
          <Grid container spacing={3}>
            {organization.additionalContacts.map((contact, index) => (
              <Grid item xs={12} md={6} key={index}>
                <ContactCard 
                  title={contact.position || "Contact"} 
                  icon={<PersonIcon color="action" />}
                >
                  <ContactItem 
                    icon={<PersonIcon fontSize="small" />}
                    primary={contact.name}
                    secondary="Name"
                  />
                  
                  {contact.email && (
                    <ContactItem 
                      icon={<EmailIcon fontSize="small" />}
                      primary={contact.email}
                      secondary="Email"
                      link={true}
                      type="email"
                    />
                  )}
                  
                  {contact.phone && (
                    <ContactItem 
                      icon={<PhoneIcon fontSize="small" />}
                      primary={contact.phone}
                      secondary="Phone"
                      link={true}
                      type="phone"
                    />
                  )}
                </ContactCard>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      
    </Grid>
  );
};

export default ContactDetailsTab; 